import Dialog from "@mui/material/Dialog";
import { styled } from "@mui/material/styles";
import { forwardRef } from "react";

import { usePageContext } from "../../context/PageContext";

/**
 * Same as a native Mui `<Dialog>`, except that responsive full-screen behavior
 * is automatically handled. You should still use all the other Dialog-related
 * components as exported from Mui.
 *
 * On mobile, the buttons will vertically stack and the padding is adjusted
 * to better fit a mobile screen.
 *
 * This is implemented [as documented by Mui](https://mui.com/material-ui/react-dialog/#responsive-full-screen).
 *
 * Don't forget, in addition to handling the Cancel action as one of the
 * `<DialogActionButton>`s, you also need to handle it on the
 * `<DreamMuiDialog>`'s `onClose`.
 */
export const DreamMuiDialog = forwardRef<HTMLDivElement, React.ComponentProps<typeof Dialog>>(
  (propsToDialog: React.ComponentProps<typeof StyledDialog>, ref) => {
    const { isMobile } = usePageContext();

    const Component = isMobile ? StyledDialog : Dialog;

    return Component ? <Component ref={ref} fullScreen={isMobile} {...propsToDialog} /> : <div />;
  }
);

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    padding: var(--horizontal-padding);
    border-radius: 1rem;
  }

  & .MuiDialogTitle-root {
    padding: 0;
  }

  & .MuiDialogContent-root {
    padding: 1rem 0 2rem 0;
  }

  & .MuiDialogActions-root {
    padding: 0;
    flex-direction: column;
    align-items: stretch;

    & > * {
      margin: 0.5rem 0 0 0;
    }
  }
`;
